import { useMemo, useState } from 'react'
import { jsPDF } from 'jspdf'
import UbuntuBase64 from '../assets/fonts/UbuntuBase64'
import UbuntuBoldBase64 from '../assets/fonts/UbuntuBoldBase64'
import logoImg from '../assets/images/logo-raudo.png'
import symbolyImg from '../assets/images/symboly.png'
import razitkoImg from '../assets/images/razitko-raudo.jpg'
import { fetchStoragePrintData } from './storagePrintData'

const toTrim = (value) => String(value ?? '').trim()
const toNum = (value, fallback = 0) => {
  const n = Number(value)
  return Number.isFinite(n) ? n : fallback
}
const yesNo = (value) => (toNum(value, 0) === 1 ? 'ano' : 'ne')
const mapSmer = (value) => {
  const n = toNum(value, -1)
  if (n === 1) return 'příjem'
  if (n === 2) return 'výdej'
  return '-'
}
const mapTypVydeje = (value) => {
  const raw = toTrim(value)
  const n = toNum(raw, -1)
  const s = raw.toLowerCase()
  if (n === 1 || s === 'montaz' || s === 'montáž') return 'montáž na vozidlo'
  if (n === 2 || s === 'vraceno' || s === 'vráceno' || s === 'vraceno zakaznikovi' || s === 'vráceno zákazníkovi') return 'vráceno zákazníkovi'
  if (n === 3 || s === 'likvidace') return 'likvidace'
  return '-'
}

const toBase64Payload = (asset) => {
  const str = String(asset || '')
  const idx = str.indexOf(',')
  return idx >= 0 ? str.slice(idx + 1) : str
}

const guessImageFormat = (dataUrl) => (String(dataUrl || '').startsWith('data:image/png') ? 'PNG' : 'JPEG')

const imageDataUrlCache = new Map()
const fileUrlToDataUrl = async (url) => {
  if (!url) return ''
  if (imageDataUrlCache.has(url)) return imageDataUrlCache.get(url)
  const response = await fetch(url)
  if (!response.ok) throw new Error(`Nepodařilo se načíst obrázek: ${url}`)
  const blob = await response.blob()
  const dataUrl = await new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => resolve(String(reader.result || ''))
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })
  imageDataUrlCache.set(url, dataUrl)
  return dataUrl
}

const formatDate = (value) => {
  if (!value) return ''
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleDateString('cs-CZ', { year: 'numeric', month: '2-digit', day: '2-digit' })
}

const registerUbuntuFonts = (doc) => {
  doc.addFileToVFS('Ubuntu-Regular.ttf', toBase64Payload(UbuntuBase64))
  doc.addFont('Ubuntu-Regular.ttf', 'Ubuntu', 'normal')
  doc.addFileToVFS('Ubuntu-Bold.ttf', toBase64Payload(UbuntuBoldBase64))
  doc.addFont('Ubuntu-Bold.ttf', 'Ubuntu', 'bold')
}

const buildProtocolPdf = async (data) => {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })
  registerUbuntuFonts(doc)
  const [logoDataUrl, symbolyDataUrl, razitkoDataUrl] = await Promise.all([
    fileUrlToDataUrl(logoImg),
    fileUrlToDataUrl(symbolyImg),
    fileUrlToDataUrl(razitkoImg),
  ])

  const pageWidth = doc.internal.pageSize.getWidth()
  const pageHeight = doc.internal.pageSize.getHeight()
  const marginX = 14
  const fullWidth = pageWidth - marginX * 2

  const firma = toTrim(data?.firma)
  const jmeno = toTrim(data?.jmeno)
  const ulice = toTrim(data?.ulice)
  const psc = toTrim(data?.psc)
  const mesto = toTrim(data?.mesto)
  const ico = toTrim(data?.ico)
  const email = toTrim(data?.email)
  const telefon = toTrim(data?.telefon)
  const klient = firma || jmeno || '-'
  const prijemZak = data?.prijemZakazka || null
  const vydejZak = data?.vydejZakazka || null
  const hasZakazkaContent = (z) => {
    if (!z || typeof z !== 'object') return false
    const textFields = [
      z?.cislo,
      z?.datum_prijmu,
      z?.datum_vydeje,
      z?.typVydeje,
      z?.pneuSummary,
      z?.rozmer,
      z?.znacka,
      z?.dezen,
      z?.sezona,
      z?.disk,
      z?.poznamka,
      z?.poznamkaVydej
    ]
    if (textFields.some((v) => toTrim(v) !== '')) return true
    return toNum(z?.pneuPocet, 0) > 0
  }
  const hasVydej = hasZakazkaContent(vydejZak)
  const hasPrijem = hasZakazkaContent(prijemZak)
  const protocolDate = formatDate(prijemZak?.datum || data?.datum || new Date()) || '-'
  const zakazka = toTrim(prijemZak?.cislo) || toTrim(data?.cisloZakazky) || '-'

  // Záhlaví
  if (logoDataUrl) doc.addImage(logoDataUrl, guessImageFormat(logoDataUrl), marginX, 12, 26, 6)
  const symbolX = pageWidth - marginX - 25
  const symbolY = 12
  doc.setFont('Ubuntu', 'bold')
  doc.setFontSize(8)
  doc.text('Autodíly • Pneu • Servis', symbolX - 2, 15, { align: 'right' })
  doc.text('www.raudo.cz', symbolX - 2, 18, { align: 'right' })
  if (symbolyDataUrl) doc.addImage(symbolyDataUrl, guessImageFormat(symbolyDataUrl), symbolX, symbolY, 25, 7)

  // Hlavička dle layoutu zakázkového listu
  const rightX = 108
  const rightW = pageWidth - rightX - marginX

  doc.setFontSize(10)
  doc.setFont('Ubuntu', 'bold')
  doc.text('RAUDO - výrobní družstvo invalidů', marginX, 32)
  doc.setFont('Ubuntu', 'normal')
  doc.setFontSize(8)
  const leftBlock = [
    'Uničovská 370/19',
    '787 01  Šumperk',
    '',
    'IČO:  26842998     DIČ:  CZ26842998',
    '',
    'zapsáno v obch. rejstříku vedeným Krajským soudem v Ostravě,',
    'oddíl Dr., vložka číslo 1797',
    '',
    'Banka:           ČSOB, a.s. / CEKO CZ PP',
    'Číslo účtu:    214745227 / 0300',
    'IBAN:             CZ84 0300 0000 0002 1474 5227',
    '',
  ]
  leftBlock.forEach((line, i) => doc.text(line, marginX, 37 + i * 4))

  doc.setFont('Ubuntu', 'bold')
  doc.setFontSize(12)
  doc.text('PROTOKOL O USKLADNĚNÍ PNEU', rightX + 3, 32)
  doc.setFontSize(9)
  doc.text(`SPZ: ${toTrim(data?.spz) || '-'}`, rightX + 3, 37)
  doc.setFont('Ubuntu', 'normal')
  doc.text(`Ze dne:     ${protocolDate}`, rightX + 3, 41)
  
  doc.setDrawColor(0)
  doc.setLineWidth(0.2)
  doc.line(rightX + 3, 45, rightX + rightW, 45)

  doc.setFont('Ubuntu', 'bold')
  doc.setFontSize(8)
  doc.text('Zákazník:', rightX + 3, 51)
  doc.text(`IČO: ${ico || '-'}`, rightX + 40, 51)
  doc.setFontSize(9)
  const addrLines = [
    klient,
    firma && jmeno ? jmeno : '',
    ulice,
    `${psc} ${mesto}`.trim()
  ].filter(Boolean)
  doc.text(addrLines[0] || '-', rightX + 3, 57)
  addrLines.slice(1).forEach((line, idx) => doc.text(line, rightX + 3, 62 + idx * 4))
  doc.setFont('Ubuntu', 'normal')
  doc.setFontSize(8)
  doc.text(`Tel.: ${telefon}`, rightX + 3, 75)
  doc.text(`E-mail: ${email}`, rightX + 3, 79)

  const drawVydejTable = (z, topY) => {
    const sectionW = fullWidth
    const unitW = sectionW / 21
    const w1 = 2.5 * unitW
    const w2 = 9 * unitW
    const w3 = 3.5 * unitW
    const w4 = 6 * unitW
    const col1LabelX = marginX
    const col1ValueX = col1LabelX + w1
    const col3LabelX = col1ValueX + w2
    const col3ValueX = col3LabelX + w3
    const typVydeje = toNum(z?.smer, 0) === 2 ? mapTypVydeje(z?.typVydeje) : '-'
    const delkaUschovy = Number.isFinite(Number(z?.delkaUschovyDny))
      ? `${Number(z.delkaUschovyDny)} dnů`
      : '-'
    const pneuText = toTrim(z?.pneuSummary) || [toTrim(z?.rozmer), toTrim(z?.znacka), toTrim(z?.dezen)].filter(Boolean).join(' ')
    const pocetKs = toNum(z?.pneuPocet, 0)
    const sezonaRaw = toTrim(z?.sezona)
    const sezonaNum = toNum(sezonaRaw, -1)
    const sezonaText =
      sezonaNum === 1 ? 'letní'
        : sezonaNum === 2 ? 'zimní'
          : sezonaNum === 3 ? 'celoroční'
            : sezonaRaw || '-'
    const diskRaw = toTrim(z?.disk)
    const diskNum = toNum(diskRaw, -1)
    const diskText =
      diskNum === 0 ? 'bez disku'
        : diskNum === 1 ? 'ocelový disk'
          : diskNum === 2 ? 'hliníkový disk'
            : diskRaw || '-'
    const poznamkaText =
      toTrim(z?.poznamka) ||
      toTrim(z?.poznamkaVydej) ||
      toTrim(data?.poznamka) ||
      toTrim(prijemZak?.poznamka) ||
      '-'

    const toSingleLine = (value, maxWidth, suffix = '...') => {
      const text = String(value || '-')
      const lines = doc.splitTextToSize(text, maxWidth)
      if (lines.length <= 1) return String(lines[0] || '-')
      return `${String(lines[0] || '').replace(/\s+$/, '')}${suffix}`
    }

    const drawInlineParts = (x, y, parts, maxWidth) => {
      let used = 0
      for (const part of parts) {
        const text = String(part?.text ?? '')
        if (!text) continue
        doc.setFont('Ubuntu', part?.bold ? 'bold' : 'normal')
        const w = doc.getTextWidth(text)
        if (used + w > maxWidth) {
          const remain = Math.max(0, maxWidth - used)
          const clipped = toSingleLine(text, remain, '')
          if (clipped) doc.text(clipped, x + used, y)
          break
        }
        doc.text(text, x + used, y)
        used += w
      }
    }

    const drawPairRow = (y, leftLabel, leftValue, rightLabel, rightValue) => {
      const leftValueWidth = w2 - 1.5
      const rightValueWidth = w4 - 1.5
      doc.setFont('Ubuntu', 'bold')
      doc.text(leftLabel, col1LabelX, y)
      doc.setFont('Ubuntu', 'normal')
      doc.text(toSingleLine(leftValue, leftValueWidth), col1ValueX, y)
      doc.setFont('Ubuntu', 'bold')
      doc.text(rightLabel, col3LabelX, y)
      doc.setFont('Ubuntu', 'normal')
      doc.text(toSingleLine(rightValue, rightValueWidth), col3ValueX, y)
    }

    doc.setFont('Ubuntu', 'bold')
    doc.setFontSize(9)
    doc.text('Výdej ze skladu', marginX, topY)

    doc.setFontSize(8)
    const startY = topY + 6
    const rowGap = 5
    const y1 = startY
    const y2 = startY + rowGap
    const y3 = startY + rowGap * 2
    const y4 = startY + rowGap * 3 + 0.5
    const y5 = y4 + rowGap

    drawPairRow(y1, 'Zakázka č.:', toTrim(z?.cislo) || '-', 'Datum uskladnění:', formatDate(z?.datum_prijmu) || '-')
    drawPairRow(y2, 'Pneumatiky:', pneuText || '-', 'Délka uskladnění:', delkaUschovy)
    drawPairRow(y3, 'Počet ks:', String(pocetKs), 'Typ výdeje:', typVydeje)

    drawInlineParts(marginX, y4, [
      { text: 'Sezóna: ', bold: true },
      { text: `${sezonaText}          `, bold: false },
      { text: 'Disk: ', bold: true },
      { text: `${diskText}          `, bold: false },
      { text: 'TMPS: ', bold: true },
      { text: `${yesNo(z?.tmps)}          `, bold: false },
      { text: 'Poklice: ', bold: true },
      { text: `${yesNo(z?.poklice)}          `, bold: false },
      { text: 'Šrouby: ', bold: true },
      { text: yesNo(z?.srouby), bold: false },
    ], sectionW)

    drawInlineParts(marginX, y5, [
      { text: 'Poznámka: ', bold: true },
      { text: poznamkaText, bold: false },
    ], sectionW)

    return y5 + 2
  }

  const drawZakazkaTable = (title, z, topY) => {
    const sectionW = fullWidth
    const unitW = sectionW / 21
    const w1 = 2.5 * unitW
    const w2 = 18.5 * unitW
    const col1LabelX = marginX
    const col1ValueX = marginX + w1

    doc.setFont('Ubuntu', 'bold')
    doc.setFontSize(9)
    doc.text(title, marginX, topY)

    const toSingleLine = (value, maxWidth, suffix = '...') => {
      const text = String(value || '-')
      const lines = doc.splitTextToSize(text, maxWidth)
      if (lines.length <= 1) return String(lines[0] || '-')
      return `${String(lines[0] || '').replace(/\s+$/, '')}${suffix}`
    }

    const drawInlineParts = (x, y, parts, maxWidth) => {
      let used = 0
      for (const part of parts) {
        const text = String(part?.text ?? '')
        if (!text) continue
        doc.setFont('Ubuntu', part?.bold ? 'bold' : 'normal')
        const w = doc.getTextWidth(text)
        if (used + w > maxWidth) {
          const remain = Math.max(0, maxWidth - used)
          const clipped = toSingleLine(text, remain, '')
          if (clipped) doc.text(clipped, x + used, y)
          break
        }
        doc.text(text, x + used, y)
        used += w
      }
    }

    const sezonaRaw = toTrim(z?.sezona)
    const sezonaNum = toNum(sezonaRaw, -1)
    const sezonaText =
      sezonaNum === 1 ? 'letní'
        : sezonaNum === 2 ? 'zimní'
          : sezonaNum === 3 ? 'celoroční'
            : sezonaRaw || '-'
    const diskRaw = toTrim(z?.disk)
    const diskNum = toNum(diskRaw, -1)
    const diskText =
      diskNum === 0 ? 'bez disku'
        : diskNum === 1 ? 'ocelový disk'
          : diskNum === 2 ? 'hliníkový disk'
            : diskRaw || '-'

    const pneuText = toTrim(z?.pneuSummary) || [toTrim(z?.rozmer), toTrim(z?.znacka), toTrim(z?.dezen)].filter(Boolean).join(' ')
    const pocetKs = toNum(z?.pneuPocet, 0)
    const poznamkaText = toTrim(z?.poznamka) || '-'

    doc.setFontSize(8)
    const startY = topY + 6
    const rowGap = 5
    const y1 = startY
    const y2 = startY + rowGap
    const y3 = startY + rowGap * 2
    const y4 = startY + rowGap * 3 + 0.5
    const y5 = y4 + rowGap

    const valueWidth = w2 - 1.5
    doc.setFont('Ubuntu', 'bold')
    doc.text('Zakázka č.:', col1LabelX, y1)
    doc.setFont('Ubuntu', 'normal')
    doc.text(toSingleLine(toTrim(z?.cislo) || '-', valueWidth), col1ValueX, y1)

    doc.setFont('Ubuntu', 'bold')
    doc.text('Pneumatiky:', col1LabelX, y2)
    doc.setFont('Ubuntu', 'normal')
    doc.text(toSingleLine(pneuText || '-', valueWidth), col1ValueX, y2)

    doc.setFont('Ubuntu', 'bold')
    doc.text('Počet ks:', col1LabelX, y3)
    doc.setFont('Ubuntu', 'normal')
    doc.text(String(pocetKs), col1ValueX, y3)

    drawInlineParts(marginX, y4, [
      { text: 'Sezóna: ', bold: true },
      { text: `${sezonaText}          `, bold: false },
      { text: 'Disk: ', bold: true },
      { text: `${diskText}          `, bold: false },
      { text: 'TMPS: ', bold: true },
      { text: `${yesNo(z?.tmps)}          `, bold: false },
      { text: 'Poklice: ', bold: true },
      { text: `${yesNo(z?.poklice)}          `, bold: false },
      { text: 'Šrouby: ', bold: true },
      { text: yesNo(z?.srouby), bold: false },
    ], sectionW)

    drawInlineParts(marginX, y5, [
      { text: 'Poznámka: ', bold: true },
      { text: poznamkaText, bold: false },
    ], sectionW)

    return y5 + 2
  }

  const firstSectionTopY = 89
  let sectionsBottomY = firstSectionTopY
  if (hasVydej) {
    sectionsBottomY = drawVydejTable(vydejZak, firstSectionTopY)
  }
  if (hasPrijem) {
    const prijemTopY = hasVydej ? sectionsBottomY + 9 : firstSectionTopY
    sectionsBottomY = drawZakazkaTable('Příjem na sklad', prijemZak, prijemTopY)
  }
  const footerBlockHeight = 30
  const bottomSafeMargin = 14
  const fy = pageHeight - bottomSafeMargin - footerBlockHeight
  const conditionsTitleY = sectionsBottomY + 9
  const CONDITIONS_BLOCK_HEIGHT = 74
  const conditionsTopY = conditionsTitleY + 5
  const conditionsBottomY = Math.min(fy - 3, conditionsTopY + CONDITIONS_BLOCK_HEIGHT)
  const conditionsColGap = 7
  const conditionsColWidth = (fullWidth - conditionsColGap) / 2
  const leftColX = marginX
  const rightColX = marginX + conditionsColWidth + conditionsColGap

  const conditionsParagraphs = [
    'Pneuservis (dále jen „skladovatel“) přebírá od zákazníka (dále jen „ukladatel“) kola nebo pneumatiky do úschovy na dobu sjednanou v protokolu o uskladnění.',
    'Specifikace uskladněných kol nebo pneumatik (rozměr, typ, počet kusů, případně hloubka dezénu a typ disků) je uvedena v protokolu o uskladnění.',
    'Skladovatel se zavazuje uskladněné věci opatrovat s běžnou odbornou péčí a uložit je tak, aby nedošlo k jejich záměně nebo zbytečnému poškození.',
    'Skladovatel odpovídá za škodu na uskladněných věcech od jejich převzetí do jejich vydání, pokud neprokáže, že škodu nemohl odvrátit ani při vynaložení běžné péče.',
    'Skladovatel neodpovídá za přirozené stárnutí pneumatik, vady pneumatik nebo disků existující již při převzetí a poškození způsobené běžným opotřebením nebo vlastnostmi materiálu.',
    'Ukladatel je povinen zaplatit sjednané skladné a vyzvednout uskladněné věci nejpozději do konce sjednaného skladovacího období.',
    'Vydání kol nebo pneumatik je možné během provozní doby skladovatele po ověření totožnosti zákazníka a po úhradě všech závazků souvisejících s uskladněním.',
    'Skladovatel je oprávněn zadržet uskladněné věci do doby zaplacení skladného nebo jiných souvisejících pohledávek.',
    'Pokud si zákazník věci nevyzvedne ani do 30 dnů po skončení sjednané doby skladování, může skladovatel stanovit dodatečnou lhůtu k jejich vyzvednutí.',
    'Po marném uplynutí této lhůty je skladovatel oprávněn uskladněné věci prodat nebo ekologicky zlikvidovat. Náklady spojené s tímto postupem jdou k tíži ukladatele.',
    'Podpisem protokolu zákazník potvrzuje předání uvedených kol nebo pneumatik do úschovy a souhlas s těmito podmínkami.'
  ]

  doc.setFont('Ubuntu', 'bold')
  doc.setFontSize(9)
  doc.text('Podmínky úschovy', marginX, conditionsTitleY)

  const fitSettings = [
    { fontSize: 7.0, lineHeight: 3.2 },
    { fontSize: 6.8, lineHeight: 3.1 },
    { fontSize: 6.6, lineHeight: 3.0 },
    { fontSize: 6.4, lineHeight: 2.9 }
  ]
  let selected = fitSettings[fitSettings.length - 1]
  for (const s of fitSettings) {
    doc.setFont('Ubuntu', 'normal')
    doc.setFontSize(s.fontSize)
    const totalLines = conditionsParagraphs.reduce((sum, p, idx) => {
      const lines = doc.splitTextToSize(p, conditionsColWidth)
      return sum + lines.length + (idx < conditionsParagraphs.length - 1 ? 1 : 0)
    }, 0)
    const linesPerCol = Math.floor((conditionsBottomY - conditionsTopY) / s.lineHeight)
    if (totalLines <= (linesPerCol * 2)) {
      selected = s
      break
    }
  }

  doc.setFont('Ubuntu', 'normal')
  doc.setFontSize(selected.fontSize)
  let currentX = leftColX
  let currentY = conditionsTopY

  conditionsParagraphs.forEach((p, idx) => {
    const lines = doc.splitTextToSize(p, conditionsColWidth)
    const blockH = lines.length * selected.lineHeight
    if (currentY + blockH > conditionsBottomY && currentX === leftColX) {
      currentX = rightColX
      currentY = conditionsTopY
    }
    if (currentY + blockH > conditionsBottomY && currentX === rightColX) return
    doc.text(lines, currentX, currentY)
    currentY += blockH
    if (idx < conditionsParagraphs.length - 1) currentY += selected.lineHeight
  })

  // Zápatí
  const stampW = 45
  const stampH = 20
  const stampX = marginX + 38
  const stampY = fy + 1

  doc.setFont('Ubuntu', 'normal')
  doc.setFontSize(8)

  if (razitkoDataUrl) {
    doc.addImage(razitkoDataUrl, guessImageFormat(razitkoDataUrl), stampX, stampY, stampW, stampH)
  }

  doc.text(`Dne: ${formatDate(new Date()) || '-'}`, marginX, fy + 22)
  const leftLineY = fy + 22
  const leftLineStart = marginX + 33
  const leftLineEnd = leftLineStart + 55
  const rightLineStart = pageWidth - marginX - 75
  const rightLineEnd = pageWidth - marginX - 10
  doc.setLineDashPattern([1, 1], 0)
  doc.line(leftLineStart, leftLineY, leftLineEnd, leftLineY)
  doc.line(rightLineStart, leftLineY, rightLineEnd, leftLineY)
  doc.setLineDashPattern([], 0)

  doc.text('Převzal do úschovy (podpis, razítko)', marginX, fy + 26)
  doc.text('Předal do úschovy (datum, podpis, razítko)', rightLineStart + 5, fy + 26, { align: 'left' })

  doc.autoPrint()
  const blobUrl = doc.output('bloburl')
  if (typeof window !== 'undefined') window.open(blobUrl, '_blank', 'noopener,noreferrer')
}

const StorageProtocolPrintTrigger = ({ zakazkaId, renderButton, disabled = false, onError }) => {
  const [isLoading, setIsLoading] = useState(false)
  const isReady = useMemo(() => toNum(zakazkaId, 0) > 0, [zakazkaId])

  const handlePrint = async (event) => {
    event?.stopPropagation?.()
    if (disabled || !isReady || isLoading) return
    setIsLoading(true)
    try {
      const data = await fetchStoragePrintData(zakazkaId)
      await buildProtocolPdf(data)
    } catch (error) {
      onError?.(error)
    } finally {
      setIsLoading(false)
    }
  }

  if (typeof renderButton === 'function') {
    return renderButton({ onClick: handlePrint, disabled: disabled || !isReady || isLoading })
  }

  return (
    <button type="button" onClick={handlePrint} disabled={disabled || !isReady || isLoading}>
      Tisk protokolu
    </button>
  )
}

export default StorageProtocolPrintTrigger
